import React, { forwardRef, type PropsWithChildren } from 'react';
import { type UseCarouselActions } from '@pkgs/components/hooks/useCarouselActions';
import ArrowRightOutlined from '@pkgs/components/assets/icons/small/Right_Arrow_Outlined.svg';
import ArrowLeftOutlined from '@pkgs/components/assets/icons/small/Left_Arrow_Outlined.svg';

import {
  DefaultCarouselContainer,
  DefaultLeftCarouselButton,
  DefaultRightCarouselButton,
  DefaultSliderContainer,
  DefaultSliderWrapper,
} from './DefaultCarouselComponents';
import { CarouselThumb as DefaultCarouselThumb, type CarouselThumbProps } from './CarouselThumb';
import { type CarouselButtonProps } from './Carousel.types';

export type CarouselProps = {
  children: React.ReactNode;
  carouselActions: UseCarouselActions;
  showNavigationButtons?: boolean;
  carouselThumbProps?: CarouselThumbProps;
  carouselContainer?: React.ComponentType<PropsWithChildren>;
  leftCarouselButton?: React.ComponentType<CarouselButtonProps>;
  rightCarouselButton?: React.ComponentType<CarouselButtonProps>;
  sliderWrapper?: React.ComponentType<PropsWithChildren>;
  carouselThumb?: React.ComponentType<CarouselThumbProps>;
  dotButton?: React.ComponentType<CarouselButtonProps>;
  sliderContainer?: React.ComponentType<PropsWithChildren>;
};

export const Carousel = forwardRef<HTMLDivElement, CarouselProps>(
  (
    {
      children,
      carouselActions,
      showNavigationButtons = true,
      carouselThumbProps,
      carouselContainer: CarouselContainer = DefaultCarouselContainer,
      leftCarouselButton: LeftCarouselButton = DefaultLeftCarouselButton,
      rightCarouselButton: RightCarouselButton = DefaultRightCarouselButton,
      sliderWrapper: SliderWrapper = DefaultSliderWrapper,
      carouselThumb: CarouselThumb = DefaultCarouselThumb,
      sliderContainer: SliderContainer = DefaultSliderContainer,
    },
    ref,
  ) => {
    const {
      selectedIndex,
      scrollSnaps,
      onDotButtonClick,
      onPreviousClick,
      onNextClick,
      emblaCarouselUtils: [emblaRef, emblaApi],
    } = carouselActions;

    return (
      <CarouselContainer ref={ref}>
        {showNavigationButtons && scrollSnaps.length > 1 && (
          <>
            <LeftCarouselButton
              label="Previous"
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
                onPreviousClick();
              }}
              icon={<ArrowLeftOutlined />}
            />
            <RightCarouselButton
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
                onNextClick();
              }}
              label="Next"
              icon={<ArrowRightOutlined />}
            />
          </>
        )}
        <SliderWrapper ref={emblaRef}>
          <SliderContainer>{children}</SliderContainer>
        </SliderWrapper>
        <CarouselThumb
          scrollSnaps={scrollSnaps}
          selectedIndex={selectedIndex}
          onDotButtonClick={onDotButtonClick}
          emblaApi={emblaApi}
          {...carouselThumbProps}
        />
      </CarouselContainer>
    );
  },
);

Carousel.displayName = 'Carousel';
